'use client';

// Canonical source for all three worker apps. scripts/sync-house-ads.mjs
// generates the tools/blog copies; edit this root file only.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { claimHouseAd, primeCountry, type HouseAdCreative } from '@/lib/house-ads';
import HouseAdSvg, { svgSizeFor } from './HouseAdSvg';

/**
 * House ad — promotes one of our own SaaS products in ad inventory AdSense
 * declined to fill. Rendered only by the unfilled-slot fallback (see
 * useAdFallback in components/useAdFallback.ts), never on its own.
 *
 * DESIGN CONSTRAINT: this must NOT look like a Google ad. AdSense policy
 * requires that other ads on the page are not formatted or placed so as to be
 * confusable with Google's. So it is labelled "From the Inventive HQ family"
 * (not "Advertisement"), carries our own brand styling, and is visibly a
 * product card rather than a display banner.
 *
 * It is a plain <a>, not next/link: it points off-site, and this component is
 * also copied into the R2-bundle-safe surfaces where next/link is forbidden.
 */
export default function HouseAd({
  pathname,
  className = '',
  blocked = false,
}: {
  pathname: string;
  className?: string;
  /** Set when the ad library was blocked rather than merely returning no ad. */
  blocked?: boolean;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [slotWidth, setSlotWidth] = useState(0);
  // Measure before paint so the slot never flashes a text card and swaps.
  useLayoutEffect(() => {
    setSlotWidth(hostRef.current?.getBoundingClientRect().width ?? 0);
  }, []);
  // Target on the BROWSER path, not the router path. The three workers have
  // different internal route bases: apps/tools serves /tools/security/xor-cipher
  // from /[category]/[slug], and apps/blog serves /blog/{slug} from /[slug], so
  // usePathname() there reports "/security/xor-cipher" and "/{slug}" with the
  // public prefix stripped. Path-prefix rules keyed to /tools or /blog would
  // therefore never match on the very workers they describe — which would both
  // break topic targeting and, worse, silently defeat the eligibility rule that
  // keeps third-party referrals off /knowledge-base and /blog.
  //
  // window.location.pathname is the real public URL on every worker. Safe to
  // read: this component only renders from useAdFallback's client-side timer,
  // so it never executes during SSR. The prop is kept as the fallback.
  const publicPath =
    typeof window !== 'undefined' ? window.location.pathname : pathname;

  // Claim once per mounted instance, so several unfilled slots on the same page
  // show several DIFFERENT products rather than repeating the best-fit one.
  //
  // Deliberately a ref, not useState's lazy initializer: StrictMode
  // double-invokes that initializer in dev and discards one result, which burns
  // a claim and made a 3-slot page render products 1, 3, 5 instead of 1, 2, 3.
  // A ref survives StrictMode's double render pass on the same fiber, so the
  // claim happens exactly once per instance in both dev and production.
  const claimRef = useRef<HouseAdCreative | null>(null);
  // Country-restricted creatives (the software store) need the visitor's
  // country, which cannot be fetched during render. Prime it for subsequent
  // pageviews; until it lands those creatives are simply not eligible.
  primeCountry();
  if (claimRef.current === null) claimRef.current = claimHouseAd(publicPath);
  const ad = claimRef.current;

  // One impression per rendered house ad, so each creative's CTR can be read
  // as house_ad_click / house_ad_view per product. Without it the only number
  // is raw clicks, which cannot tell a weak creative from a mistargeted one.
  useEffect(() => {
    try {
      const w = window as unknown as {
        zaraz?: { track?: (n: string, p: Record<string, unknown>) => void };
        gtag?: (...args: unknown[]) => void;
      };
      const payload = { product: ad.trackingId, path: publicPath, blocked };
      if (w.zaraz?.track) w.zaraz.track('house_ad_view', payload);
      else w.gtag?.('event', 'house_ad_view', payload);
    } catch {
      // Never throw into the page over analytics.
    }
    // Once per mounted instance — the claim is fixed for the instance's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const track = () => {
    // Best-effort click signal. The authoritative conversion measurement is the
    // utm_medium=house-ad attribution in the destination app's own analytics —
    // this event only gives us on-site CTR per product.
    try {
      const w = window as unknown as {
        zaraz?: { track?: (n: string, p: Record<string, unknown>) => void };
        gtag?: (...args: unknown[]) => void;
      };
      const payload = { product: ad.trackingId, path: publicPath };
      w.zaraz?.track?.('house_ad_click', payload);
      w.gtag?.('event', 'house_ad_click', payload);
    } catch {
      // Never throw into the page over analytics.
    }
  };

  const isReferral = ad.kind === 'referral';

  // Generated SVG artwork for our own products. Inline, so it renders for
  // blocked visitors where no external image could, and it holds attention in a
  // way the text card does not. Referrals keep the text card here — this app has
  // no network banner wired up yet, and inventing artwork for someone else's
  // brand is not ours to do.
  const svgSize = ad.kind === 'owned' ? svgSizeFor(slotWidth) : null;
  if (svgSize) {
    return (
      <div ref={hostRef} className={className}>
        <div className="mb-2 select-none text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
          From the Inventive HQ family
        </div>
        <a
          href={ad.href}
          onClick={track}
          target="_blank"
          rel="nofollow noopener"
          data-house-ad={ad.trackingId}
          data-house-ad-kind={ad.kind}
          className="block no-underline"
        >
          <HouseAdSvg creative={ad} size={svgSize} />
        </a>
      </div>
    );
  }

  return (
    <div ref={hostRef}>
    <a
      href={ad.href}
      onClick={track}
      target="_blank"
      // Referral links earn us a reward, so they get rel="sponsored" per Google's
      // link-attribution guidance — passing PageRank to a paid placement is a
      // link-scheme violation.
      rel={isReferral ? 'sponsored nofollow noopener' : 'nofollow noopener'}
      // Stable hook for debugging and verification. Referral hrefs deliberately
      // carry no UTM, so the href alone can't identify a house ad any more.
      data-house-ad={ad.trackingId}
      data-house-ad-kind={ad.kind}
      className={`group block rounded-xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-5 no-underline transition-colors hover:border-[#2813e8] dark:border-gray-700 dark:from-gray-800 dark:to-gray-900 dark:hover:border-[#2cc3d1] ${className}`}
    >
      {/* Referrals must never claim to be one of our brands. "From the
          Inventive HQ family" on a third-party financial product would be a
          plain misstatement, and a referral needs a conspicuous disclosure of
          the fact we are compensated. */}
      <div className="mb-2 select-none text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {isReferral ? 'Referral link · we earn a reward' : 'From the Inventive HQ family'}
      </div>
      <div className="text-base font-bold text-gray-900 dark:text-gray-100">
        {ad.name}
      </div>
      <div className="mt-1 text-sm font-medium text-[#2813e8] dark:text-[#2cc3d1]">
        {ad.tagline}
      </div>
      <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
        {ad.description}
      </p>
      {/* Per-creative fine print — currently the risk disclosure the financial
          referral's own destination carries. Keyed to the creative rather than
          to kind === 'referral': that earlier shortcut printed "All investments
          involve risk" under a voice-AI ad, which is noise at best and reads as
          boilerplate nobody checked at worst. */}
      {ad.fineprint && (
        <p className="mt-2 text-[11px] leading-snug text-gray-400 dark:text-gray-500">
          {ad.fineprint}
        </p>
      )}
      <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#2813e8] group-hover:gap-2 dark:text-[#2cc3d1]">
        Take a look
        <span aria-hidden="true">→</span>
      </span>
    </a>
    </div>
  );
}
